/**
 * Default filter options served immediately (and as a resilient fallback if
 * the constants query fails), then refined from the API in useGiftcardConstants.
 * Not test data — these keep the filter dropdowns usable before/without a
 * successful constants fetch.
 */
import type { GiftcardConstants } from './types';

/**
 * The `state_transitions.reason` value reware-api writes for an audited reveal, from
 * lib/graphql/admin/queries/giftcard/sensitiveDetails.js. It is a bare literal there — not a
 * schema enum and not an exported constant — so nothing detects drift. Named here so there is
 * one place to change, and pinned by a test.
 *
 * Drift fails in two directions at once: the Reveal History tab stops existing (ActivityTabs
 * hides zero-count tabs), and the reveals leak into History, where the columns rendered do not
 * include `note` — the only field saying which secret was shown.
 */
export const REVEAL_TRANSITION_REASON = 'revealed';

export const DEFAULT_GIFTCARD_STATES: GiftcardConstants['states'] = [
  { code: 'ISSUED', displayName: 'Issued' },
  { code: 'COMPLETE', displayName: 'Redeemed' },
  { code: 'AUTH', displayName: 'Authorized but not redeemed' },
  { code: 'AUTH_FAILED', displayName: 'Authorization failed (GC failure)' },
  { code: 'CANCELED', displayName: 'GC Canceled' },
  { code: 'CANCELED_IN_STORE', displayName: 'GC Canceled in store' },
  { code: 'PENDING_STRIPE', displayName: 'Pending Stripe' },
  { code: 'REISSUED', displayName: 'Reissued' },
];
export const DEFAULT_GIFTCARD_SOURCES: GiftcardConstants['sources'] = [
  { code: 'PAYMENT', displayName: 'Payment' },
  { code: 'ISSUED_REFUND', displayName: 'Issued Refund' },
  { code: 'ISSUED_MANUAL', displayName: 'Issued Manual' },
  { code: 'ISSUED_MITI', displayName: 'Issued Digital Trade-in (DITI)' },
  { code: 'ISSUED_INSTI', displayName: 'Issued In-store (INSTI)' },
];
export const DEFAULT_GIFTCARD_REASONS: GiftcardConstants['reasons'] = [
  { code: 'SHIPPING_ISSUE', displayName: 'Shipping Issue' },
  { code: 'CS_ISSUE', displayName: 'Customer Service Issue' },
  { code: 'PROMOTIONS', displayName: 'Promotions' },
  { code: 'OTHER', displayName: 'Other' },
];
